import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Tag, User, ShoppingBag, Heart, Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useCart } from "@/context/CartContext";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { customerApi } from "@/lib/api";
import { CurrencySelector } from "@/context/CurrencyContext";

const NAV = [
  { label: "Home", to: "/" },
  { label: "All", to: "/shop/all" },
  { label: "New In", to: "/shop/new-in" },
  { label: "Necklaces", to: "/shop/necklace-sets" },
  { label: "Bridal Sets", to: "/shop/bridal-sets" },
  { label: "Bangles", to: "/shop/bangles" },
  { label: "Earrings", to: "/shop/earrings" },
  { label: "Sale", to: "/shop/sale", sale: true },
  { label: "Contact", to: "/contact" },
];

export const Navbar = () => {
  const { cart, setDrawerOpen, wishlist } = useCart();
  const { customer } = useCustomerAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [offerCount, setOfferCount] = useState(0);

  useEffect(() => {
    if (!customer) { setOfferCount(0); return; }
    customerApi().get("/account/offers-summary")
      .then(({ data }) => setOfferCount(data.available || 0))
      .catch(() => setOfferCount(0));
  }, [customer]);

  return (
    <>
    <header className="z-50 bg-white/95 backdrop-blur-md border-b border-gray-100" data-testid="navbar">
      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[72px] gap-4">
          <button
            className="lg:hidden text-charcoal"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-button"
          >
            <Menu className="w-6 h-6" />
          </button>

          <Logo className="shrink-0" />

          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `relative text-xs font-sans uppercase tracking-[0.15em] transition-colors duration-300 py-1.5 group ${
                    item.sale ? "text-pink" : "text-charcoal hover:text-pink"
                  } ${isActive && !item.sale ? "text-pink" : ""}`
                }
              >
                {item.label}
                <span className="absolute left-0 -bottom-0.5 h-px w-0 bg-pink transition-all duration-300 group-hover:w-full" />
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-4 sm:gap-5 text-charcoal">
            <CurrencySelector />
            <Link to="/offers" data-testid="offers-link" className="relative hidden sm:flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] hover:text-pink transition-colors">
              <span className="relative">
                <Tag className="w-4 h-4" />
                {offerCount > 0 && (
                  <span data-testid="offers-notification-dot"
                    className="absolute -top-1.5 -right-1.5 bg-gold-bright text-charcoal text-[9px] font-bold w-[15px] h-[15px] rounded-full flex items-center justify-center ring-2 ring-white">
                    {offerCount}
                  </span>
                )}
              </span>
              <span className="hidden sm:inline">Offers</span>
            </Link>
            <Link to="/wishlist" aria-label="Wishlist" data-testid="wishlist-link" className="relative hover:text-pink transition-colors">
              <Heart className="w-5 h-5" />
              {wishlist.length > 0 && (
                <span
                  className="absolute -top-2 -right-2 bg-pink text-white text-[10px] font-sans w-[18px] h-[18px] rounded-full flex items-center justify-center"
                  data-testid="wishlist-count-badge"
                >
                  {wishlist.length}
                </span>
              )}
            </Link>
            <Link to={customer ? "/account" : "/login"} aria-label="Account" data-testid="account-link" className="hidden sm:flex items-center gap-1.5 hover:text-pink transition-colors">
              <User className="w-5 h-5" />
              {customer && <span className="text-xs uppercase tracking-[0.12em] max-w-[90px] truncate">{customer.name?.split(" ")[0] || "Account"}</span>}
            </Link>
            <button onClick={() => setDrawerOpen(true)} aria-label="Cart" data-testid="cart-button" className="relative hover:text-pink transition-colors">
              <ShoppingBag className="w-5 h-5" />
              {cart.count > 0 && (
                <span
                  className="absolute -top-2 -right-2 bg-pink text-white text-[10px] font-sans w-[18px] h-[18px] rounded-full flex items-center justify-center"
                  data-testid="cart-count-badge"
                >
                  {cart.count}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </header>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-[82%] max-w-xs bg-white shadow-2xl flex flex-col animate-slide-in-left">
            <div className="flex items-center justify-between px-6 h-[72px] border-b border-gray-100 shrink-0">
              <Logo />
              <button onClick={() => setMobileOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close">
                <X className="w-6 h-6 text-charcoal" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain flex flex-col">
              <nav className="flex flex-col px-6 py-2">
                {NAV.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `py-3.5 border-b border-gray-100 text-sm uppercase tracking-[0.14em] transition-colors ${
                        item.sale ? "text-pink font-medium" : isActive ? "text-pink" : "text-charcoal hover:text-pink"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
              </nav>
              <div className="mt-auto px-6 py-5 border-t border-gray-100 flex flex-col gap-4">
                <Link
                  to={customer ? "/account" : "/login"}
                  onClick={() => setMobileOpen(false)}
                  data-testid="mobile-account-link"
                  className="flex items-center gap-2.5 text-sm uppercase tracking-[0.12em] text-charcoal hover:text-pink transition-colors"
                >
                  <User className="w-5 h-5" /> {customer ? (customer.name?.split(" ")[0] || "My Account") : "Login / Register"}
                </Link>
                <Link
                  to="/offers"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2.5 text-sm uppercase tracking-[0.12em] text-charcoal hover:text-pink transition-colors"
                >
                  <Tag className="w-5 h-5" /> Offers
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
